import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Save, RotateCcw, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AGENT_PROMPT } from '@/config/agent-tools';

export default function SystemPromptEditor() {
  const { toast } = useToast();
  const { orgId } = useAuth();
  const [prompt, setPrompt] = useState(AGENT_PROMPT);
  const [originalPrompt, setOriginalPrompt] = useState(AGENT_PROMPT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (orgId) loadOrgAndPrompt();
  }, [orgId]);

  const loadOrgAndPrompt = async () => {
    if (!orgId) return;
    
    // Load custom prompt from preferences
    const { data } = await supabase.functions.invoke('agent-memory-service', {
      body: {
        action: 'get_preferences',
        data: { org_id: orgId }
      }
    });

    if (data?.preferences?.system_prompt) {
      const customPrompt = data.preferences.system_prompt.value;
      setPrompt(customPrompt);
      setOriginalPrompt(customPrompt);
    }
  };

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);

    try {
      const { error } = await supabase.functions.invoke('agent-memory-service', {
        body: {
          action: 'set_preference',
          data: {
            org_id: orgId,
            key: 'system_prompt',
            value: prompt,
            confidence: 1.0
          }
        }
      });

      if (error) throw error;

      setOriginalPrompt(prompt);
      toast({
        title: 'System Prompt Updated',
        description: 'The agent will use this prompt in all future conversations',
      });
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast({
        title: 'Error',
        description: 'Failed to save system prompt',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPrompt(originalPrompt);
  };

  const handleResetToDefault = () => {
    setPrompt(AGENT_PROMPT);
  };

  const hasChanges = prompt !== originalPrompt;

  return (
    <Card>
      <CardHeader>
        <CardTitle>System Prompt Configuration</CardTitle>
        <CardDescription>
          Define how Bridgette behaves, her personality, capabilities, and constraints. This prompt is used in all voice and text interactions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="min-h-[400px] font-mono text-sm"
          placeholder="You are Bridgette, an elite AI roofing assistant..."
        />
        
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            
            <Button
              onClick={handleReset}
              disabled={!hasChanges}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>

            <Button
              onClick={handleResetToDefault}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Default
            </Button>
          </div>

          <div className="text-sm text-muted-foreground">
            {prompt.length} characters
          </div>
        </div>

        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Eye className="w-4 h-4" />
            Current Active Prompt Preview
          </div>
          <p className="text-xs text-muted-foreground line-clamp-3">
            {originalPrompt}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
